import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { Send, CheckCircle2, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'general', label: 'General question' },
  { id: 'billing', label: 'Billing & subscription' },
  { id: 'bug', label: 'Report a bug' },
  { id: 'feature', label: 'Feature request' },
  { id: 'abuse', label: 'Report abuse / content concern' },
];

export default function ContactPage() {
  const { user, token } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
    category: 'general',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    document.title = 'Contact Support · Delphi AI';
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || form.message.trim().length < 10) {
      toast.error('Please complete every field (message must be at least 10 characters).');
      return;
    }
    setSending(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/support/contact`, form, { headers });
      setSent(true);
      toast.success("Message sent — we'll get back to you within 24 hours.");
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send — please email support@delphiai.ca directly.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="contact-page">
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-3xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-white" /></div>
              <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact Support</h1>
            </div>
            <p className="text-sm text-white/50 ml-13">We respond within 24 hours. For instant answers, check our <Link to="/help" className="text-orange-400 hover:text-orange-300 underline underline-offset-2">Help Center</Link>.</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-8">
          {sent ? (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-8 text-center" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Message sent</h2>
              <p className="text-sm text-white/60 mb-6 max-w-md mx-auto">Thanks for reaching out — we&apos;ve received your message and will reply within 24 hours. Check your inbox.</p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={() => { setSent(false); setForm(f => ({ ...f, subject: '', message: '' })); }} data-testid="contact-send-another">
                  Send another
                </Button>
                <Link to="/dashboard"><Button className="btn-oracle">Back to Dashboard</Button></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 rounded-xl border border-white/[0.06] bg-[#12121a]/60 p-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Your name</label>
                  <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Alex Founder" data-testid="contact-name" className="bg-black/40 border-white/[0.06] text-white" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Email</label>
                  <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="alex@brand.com" data-testid="contact-email" className="bg-black/40 border-white/[0.06] text-white" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Category</label>
                  <Select value={form.category} onValueChange={v => set('category', v)}>
                    <SelectTrigger className="bg-black/40 border-white/[0.06] text-white" data-testid="contact-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#12121a] border-white/10 text-white">
                      {CATEGORIES.map(c => (<SelectItem key={c.id} value={c.id} data-testid={`category-${c.id}`}>{c.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Subject</label>
                  <Input value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Quick question about credits" data-testid="contact-subject" className="bg-black/40 border-white/[0.06] text-white" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block">Message</label>
                <Textarea value={form.message} onChange={e => set('message', e.target.value)} placeholder="Tell us as much detail as you can — screenshots, what you tried, what you expected..." data-testid="contact-message"
                  className="bg-black/40 border-white/[0.06] text-white min-h-[160px] resize-none" />
                <p className="text-[10px] text-white/30 mt-1">{form.message.length}/4000 characters</p>
              </div>

              <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full btn-oracle">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending</> : <><Send className="w-4 h-4 mr-2" />Send Message</>}
              </Button>
              <p className="text-[11px] text-white/30 text-center">Or email us directly at <a href="mailto:support@delphiai.ca" className="text-white/60 hover:text-white">support@delphiai.ca</a></p>
            </form>
          )}
        </div>
      </div>
    </AppShell>
  );
}
